import { Button, Spinner } from '@wwtr/components/ui';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  GoogleMap,
  InfoBox,
  Marker,
  useJsApiLoader,
} from '@react-google-maps/api';
import { LocationMarkerIcon } from '@heroicons/react/solid';
import NextLink from 'next/link';
import cn from 'classnames';

export type OrganizationListingMapProps = {
  entries: MapEntry[];
  isLoading: boolean;
  selectedLocation: any;
};

export type MapEntry = {
  id: string;
  title: string;
  lat: number;
  lng: number;
  address?: {
    address?: string;
    number?: string;
    postcode?: string;
    city?: string;
  };
  uri?: string;
};

const center = {
  lat: 52.4395737,
  lng: 6.5126433,
};

const setMarkerIcon = marker => {
  marker.setIcon({
    path: 'M96.571,90.9886 C104.93,81.3416 110,68.7666 110,54.9996 C110,24.6236 85.375,-0.0004 55,-0.0004 C24.624,-0.0004 0,24.6236 0,54.9996 C0,68.7666 5.07,81.3416 13.429,90.9886 L13.418,90.9996 L13.572,91.1536 C15.209,93.0286 16.971,94.7906 18.846,96.4276 L55,132.5816 L91.156,96.4266 C93.029,94.7896 94.79,93.0296 96.426,91.1556 L96.582,90.9996 L96.571,90.9886 Z',
    fillColor: '#ff0000',
    fillOpacity: 1,
    scale: 0.2,
    strokeColor: '#FFF',
    strokeWeight: 1,
    strokeOpacity: 1,
    anchor: new window.google.maps.Point(55, 132),
  });
};

const getMarkerInfoWindow = entry => (
  <div className="pt-1 pb-2 px-2 font-body ">
    <h1 className="font-semibold text-powerblue text-xl mb-2">{entry.title}</h1>
    {entry?.address?.address && (
      <div
        className="text-sm text-gray-500"
        title={`Bezoekadres ${entry.title}`}
      >
        <address>
          <LocationMarkerIcon className="inline align-text-top w-4 h-4 mr-1" />
          <div className="inline">
            {entry?.address?.address} {entry?.address?.number},{' '}
            {entry?.address?.city}
          </div>
        </address>
      </div>
    )}
  </div>
);

export const OrganizationListingMap = memo(
  ({
    entries: markers,
    isLoading,
    selectedLocation,
  }: OrganizationListingMapProps) => {
    const mapRef = useRef(null);
    const { isLoaded: isGoogleMapsLoaded, loadError: googleMapsLoadError } =
      useJsApiLoader({
        id: 'google-map-script',
        version: 'weekly',
        googleMapsApiKey: 'AIzaSyALAoCzT99hIXU-2VR94P8Bdhz2xp4IWXY',
        preventGoogleFontsLoading: false,
      });

    const [map, setMap] = useState(null);
    const [selectedMapEntry, setSelectedMapEntry] = useState(null);
    const [infoBoxFixed, setInfoBoxFixed] = useState(false);
    const [infoBoxOpen, setInfoBoxOpen] = useState(false);

    const onLoad = useCallback(map => {
      setMap(map);
    }, []);

    const onUnmount = useCallback(map => {
      setMap(null);
    }, []);

    const onMarkerClick = useCallback(
      event => {
        setInfoBoxFixed(!infoBoxFixed);
      },
      [infoBoxFixed]
    );

    const onMarkerMouseMove = useCallback(
      (event, entry) => {
        if (infoBoxFixed && selectedMapEntry.id !== entry.id) {
          setInfoBoxFixed(false);
        }
        setSelectedMapEntry(entry);
        setInfoBoxOpen(true);
      },
      [infoBoxFixed, setSelectedMapEntry, setInfoBoxOpen, setInfoBoxFixed]
    );

    const onMarkerMouseOut = useCallback(
      event => {
        if (!infoBoxFixed) {
          setInfoBoxOpen(false);
        }
      },
      [setInfoBoxOpen, infoBoxFixed]
    );

    const closeInfoWindow = useCallback(() => {
      setInfoBoxFixed(false);
      setInfoBoxOpen(false);
    }, [setInfoBoxOpen, setInfoBoxFixed]);

    // Fit bounds function
    const fitBounds = () => {
      if (markers.length > 0) {
        const bounds = new window.google.maps.LatLngBounds();
        markers?.map(item => {
          bounds.extend({ lat: item.lat, lng: item.lng });
          return item.id;
        });

        map && map.fitBounds(bounds);
      }
    };

    useEffect(() => {
      if (markers) {
        setInfoBoxOpen(false);
        setInfoBoxFixed(false);

        fitBounds();
      }
    }, [markers, setInfoBoxOpen, setInfoBoxFixed]);

    if (googleMapsLoadError) {
      // eslint-disable-next-line no-console
      console.log('error', googleMapsLoadError);

      return null;
    }

    if (!isGoogleMapsLoaded) return null;

    return (
      <>
        {isLoading && (
          <div className="absolute w-full top-0 right-0 bottom-0 left-0 bg-white bg-opacity-40 z-30 transition-all duration-300 flex flex-col items-center justify-center">
            <Spinner radius={40} thickness={4} className="text-powerblue" />
          </div>
        )}

        <GoogleMap
          ref={mapRef}
          mapContainerClassName="w-full h-full"
          center={center}
          zoom={12}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={closeInfoWindow}
          options={{
            scrollwheel: false,
            disableDefaultUI: true, // a way to quickly hide all controls
            mapTypeControl: false,
            scaleControl: false,
            zoomControl: true,
            styles: [
              {
                featureType: 'poi',
                elementType: 'labels.icon',
                stylers: [
                  {
                    visibility: 'off',
                  },
                ],
              },
            ],
          }}
        >
          {!isLoading &&
            markers?.map((entry, index) => {
              const isActive = entry.id === selectedLocation;
              return (
                <Marker
                  key={`google_maps_marker_${entry.id}_${index}`}
                  // label={{
                  //   text: 'A',
                  //   color: '#ffffff',
                  //   fontSize: isActive ? '1.4rem' : '0.8rem',
                  // }}
                  position={{
                    lat: entry.lat,
                    lng: entry.lng,
                  }}
                  title={entry.title}
                  onMouseOver={event => onMarkerMouseMove(event, entry)}
                  onMouseOut={onMarkerMouseOut}
                  onClick={onMarkerClick}
                  icon={{
                    path: 'M96.571,90.9886 C104.93,81.3416 110,68.7666 110,54.9996 C110,24.6236 85.375,-0.0004 55,-0.0004 C24.624,-0.0004 0,24.6236 0,54.9996 C0,68.7666 5.07,81.3416 13.429,90.9886 L13.418,90.9996 L13.572,91.1536 C15.209,93.0286 16.971,94.7906 18.846,96.4276 L55,132.5816 L91.156,96.4266 C93.029,94.7896 94.79,93.0296 96.426,91.1556 L96.582,90.9996 L96.571,90.9886 Z',
                    fillColor: isActive ? '#F26E21' : '#2a378d',
                    fillOpacity: 1,
                    scale: isActive ? 0.35 : 0.2,
                    strokeColor: '#FFF',
                    strokeWeight: isActive ? 2 : 1,
                    strokeOpacity: 1,
                    anchor: new window.google.maps.Point(55, 132),
                    labelOrigin: isActive
                      ? new window.google.maps.Point(54, 54)
                      : new window.google.maps.Point(54, 54),
                  }}
                  zIndex={isActive ? 1000 : undefined}
                />
              );
            })}

          {infoBoxOpen && selectedMapEntry && (
            <InfoBox
              position={
                new window.google.maps.LatLng({
                  lat: selectedMapEntry.lat,
                  lng: selectedMapEntry.lng,
                })
              }
              options={{
                boxClass: 'w-96 bg-white rounded-sm shadow-lg p-4',
                maxWidth: 384,
                alignBottom: true,
                pixelOffset: new window.google.maps.Size(
                  -192,
                  infoBoxFixed ? -34 : -34
                ),
                infoBoxClearance: new window.google.maps.Size(20, 20),
                closeBoxURL: null,
                enableEventPropagation: true,
              }}
              onCloseClick={closeInfoWindow}
            >
              <div
                className={cn(
                  'font-body transform transition-all duration-200',
                  { 'translate-y-0 mt-0': infoBoxFixed },
                  { 'translate-y-16 -mt-16': !infoBoxFixed }
                )}
              >
                {getMarkerInfoWindow(selectedMapEntry)}
                {selectedMapEntry.uri && (
                  <NextLink href={`/${selectedMapEntry.uri}`} passHref>
                    <Button
                      as="a"
                      color="powerblue"
                      size="sm"
                      appearance="solid"
                      fullWidth={true}
                      className="mt-5"
                      title={`Lees meer over ${selectedMapEntry.title}`}
                    >
                      Naar detailpagina
                    </Button>
                  </NextLink>
                )}
              </div>
            </InfoBox>
          )}
        </GoogleMap>
      </>
    );
  }
);

OrganizationListingMap.displayName = 'OrganizationListingMap';
